import { useMemo } from 'react';
import { addMonths } from 'date-fns/addMonths';
import { startOfMonth } from 'date-fns/startOfMonth';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Button, HrefLink, SingleLineText, Tag } from '@bloomays-lib/ui.shared';
import { PurchaseOrderMissionCoverage } from '@bloomays-lib/types.shared';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import {
  COVERAGE_MONTH_OFFSET,
  CoverageMonthSlotKey,
  DashboardPurchaseOrder,
  derivePurchaseOrderDisplayStatus,
  MissionEnrichment,
  MonthCoverageDetail,
  purchaseOrdersLinkedToMission,
  resolveMonthCoverageDetail,
  PurchaseOrderDisplayStatus,
} from '../../helpers/purchaseOrderDashboard';
import { getPurchaseOrderDisplayStatusPalette } from '../../helpers/purchaseOrderSemanticStatus';

export type PurchaseOrderCoverageDetailPanelProps = {
  open: boolean;
  coverage?: PurchaseOrderMissionCoverage;
  enrichment?: MissionEnrichment;
  purchaseOrders: DashboardPurchaseOrder[];
  referenceDate: Date;
  onClose: () => void;
  onCreateBdc: (coverage: PurchaseOrderMissionCoverage) => void;
  onEditPurchaseOrder: (purchaseOrder: DashboardPurchaseOrder) => void;
};

const COVERAGE_MONTH_KEYS: CoverageMonthSlotKey[] = ['current', 'monthPlus1', 'monthPlus2', 'monthPlus3'];

const formatAmount = (value: number | undefined | null): string => {
  if (value === undefined || value === null) {
    return '—';
  }
  return `${Math.round(value).toLocaleString('fr-FR')} €`;
};

const formatDate = (value: string | Date | undefined | null): string => {
  if (!value) {
    return '—';
  }
  return convertDateToStringFormat(value as string, 'dd/MM/yyyy');
};

const formatPeriod = (start?: string, end?: string): string => {
  if (!start && !end) {
    return '—';
  }
  return `${formatDate(start)} → ${formatDate(end)}`;
};

const formatTjm = (tjm: number | undefined): string => {
  if (tjm === undefined) {
    return '—';
  }
  return `${tjm.toLocaleString('fr-FR')} €/j`;
};

const formatAccountManager = (accountManager: string | undefined): string => {
  if (!accountManager) {
    return '—';
  }
  const parts = accountManager.trim().split(/\s+/);
  if (parts.length < 2) {
    return accountManager;
  }
  return `${parts[0].charAt(0)}. ${parts.slice(1).join(' ')}`;
};

export const PurchaseOrderCoverageDetailPanel = ({
  open,
  coverage,
  enrichment,
  purchaseOrders,
  referenceDate,
  onClose,
  onCreateBdc,
  onEditPurchaseOrder,
}: PurchaseOrderCoverageDetailPanelProps) => {
  const { t } = useTranslation(['purchaseOrderDashboard']);
  const theme = useTheme();

  const statusLabel = useMemo<Record<PurchaseOrderDisplayStatus, string>>(
    () => ({
      received: t('statusReceived'),
      partial: t('statusPartial'),
      waiting: t('statusWaiting'),
      expired: t('statusExpired'),
      soldout: t('statusSoldout'),
    }),
    [t],
  );

  const monthCoverage = useMemo(() => {
    if (!coverage) {
      return [];
    }
    return COVERAGE_MONTH_KEYS.map((key) => {
      const monthDate = convertDateToStringFormat(
        startOfMonth(addMonths(referenceDate, COVERAGE_MONTH_OFFSET[key])),
        'MMM yy',
      );
      const detail = resolveMonthCoverageDetail({
        missionRecordId: coverage.missionRecordId,
        beginMission: enrichment?.beginMission,
        endMission: enrichment?.endMission,
        purchaseOrders,
        referenceDate,
        monthOffset: COVERAGE_MONTH_OFFSET[key],
      });
      return { key, monthDate, detail };
    });
  }, [coverage, enrichment, purchaseOrders, referenceDate]);

  const linkedPurchaseOrders = useMemo(
    () => (coverage ? purchaseOrdersLinkedToMission(purchaseOrders, coverage.missionRecordId) : []),
    [coverage, purchaseOrders],
  );

  const clientName = enrichment?.clientSocietyName ?? coverage?.clientSocietyName ?? '—';
  const bloomerName = enrichment?.bloomerFullName ?? '—';

  const renderMonthStatus = (detail: MonthCoverageDetail) => {
    if (detail.status === 'inactive') {
      return <MutedText variant="body2">{t('coverageDetailInactive')}</MutedText>;
    }
    if (detail.status === 'covered') {
      return (
        <CoveredStatus>
          <CheckIcon sx={{ fontSize: 16, color: '#16a34a' }} />
          <Typography variant="body2">{t('coverageDetailCoveredWithRef', { ref: detail.orderNumber })}</Typography>
        </CoveredStatus>
      );
    }
    return <WarningText variant="body2">{t('coverageNotCovered')}</WarningText>;
  };

  if (!coverage) {
    return null;
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Panel>
        <Header>
          <HeaderText>
            <MissionLink
              href={`/missions/${coverage.missionRecordId}`}
              target="_self"
              title={t('coverageDetailViewMission')}
              text={coverage.missionLabel ?? coverage.missionRecordId}
              color="secondary"
            />
            <Subtitle variant="body2Light">
              {clientName} — {bloomerName}
            </Subtitle>
          </HeaderText>
          <IconButton aria-label="close-coverage-detail" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Header>

        <Section>
          <SectionTitle text={t('coverageDetailSectionMission')} />
          <DetailGrid>
            <DetailRow label={t('detailClient')} value={clientName} />
            <DetailRow label={t('detailBloomer')} value={bloomerName} />
            <DetailRow label={t('detailAm')} value={formatAccountManager(enrichment?.accountManager)} />
            <DetailRow label={t('detailTjm')} value={formatTjm(enrichment?.tjm)} />
            <DetailRow
              label={t('detailPeriod')}
              value={formatPeriod(enrichment?.beginMission, enrichment?.endMission)}
            />
          </DetailGrid>
        </Section>

        <Section>
          <SectionTitle text={t('coverageDetailSectionCoverage')} />
          <MonthList>
            {monthCoverage.map((month) => (
              <MonthRow key={month.key}>
                <MonthLabel variant="body2">{month.monthDate}</MonthLabel>
                {renderMonthStatus(month.detail)}
              </MonthRow>
            ))}
          </MonthList>
        </Section>

        <Section>
          <SectionTitle text={t('coverageDetailSectionPurchaseOrders', { count: linkedPurchaseOrders.length })} />
          {linkedPurchaseOrders.length === 0 ? (
            <MutedText variant="body2">{t('coverageDetailNoPurchaseOrders')}</MutedText>
          ) : (
            <PurchaseOrderList>
              {linkedPurchaseOrders.map((purchaseOrder) => {
                const displayStatus = derivePurchaseOrderDisplayStatus(purchaseOrder);
                const palette = getPurchaseOrderDisplayStatusPalette(theme, displayStatus);
                return (
                  <PurchaseOrderCard
                    key={purchaseOrder.recordId}
                    title={t('editAction')}
                    onClick={() => onEditPurchaseOrder(purchaseOrder)}
                  >
                    <PurchaseOrderHeader>
                      <Typography variant="body2">{purchaseOrder.orderNumber}</Typography>
                      <AmountText variant="body2">{formatAmount(purchaseOrder.amountHT)}</AmountText>
                    </PurchaseOrderHeader>
                    <PeriodText variant="caption">
                      {formatPeriod(
                        purchaseOrder.startDate as string | undefined,
                        purchaseOrder.endDate as string | undefined,
                      )}
                    </PeriodText>
                    <TagRow>
                      <Tag
                        text={statusLabel[displayStatus]}
                        backgroundColor={palette.background}
                        color={palette.color}
                      />
                    </TagRow>
                  </PurchaseOrderCard>
                );
              })}
            </PurchaseOrderList>
          )}
        </Section>

        <Footer>
          <Button
            ariaLabel="coverage-detail-create-bdc"
            textButton={t('createBdcForMission')}
            onClick={() => onCreateBdc(coverage)}
          />
        </Footer>
      </Panel>
    </Drawer>
  );
};

type DetailRowProps = {
  label: string;
  value: string;
};

const DetailRow = ({ label, value }: DetailRowProps) => (
  <DetailRowContainer>
    <DetailLabel variant="caption">{label}</DetailLabel>
    <DetailValue variant="body2">{value}</DetailValue>
  </DetailRowContainer>
);

export default PurchaseOrderCoverageDetailPanel;

const Panel = styled('div')(({ theme }) => ({
  width: 'min(420px, 100vw)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '24px 20px 20px',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.neutral.white,
}));

const Header = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 20,
  paddingBottom: 16,
  borderBottom: '1px solid #e5e7eb',
}));

const HeaderText = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
}));

const MissionLink = styled(HrefLink)(({ theme }) => ({
  textDecoration: 'none',
  '& .MuiTypography-root': {
    fontWeight: 600,
    fontSize: '1.1rem',
    lineHeight: 1.3,
    color: theme.palette.blue?.[60] ?? '#2563eb',
  },
  '&:hover .MuiTypography-root': {
    textDecoration: 'underline',
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#6b7280',
}));

const Section = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginBottom: 20,
}));

const SectionTitle = styled(SingleLineText)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral?.[50] ?? '#9ca3af',
}));

const DetailGrid = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
}));

const DetailRowContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 12,
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#6b7280',
  flexShrink: 0,
}));

const DetailValue = styled(Typography)(() => ({
  fontWeight: 600,
  textAlign: 'right',
}));

const MonthList = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
}));

const MonthRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
}));

const MonthLabel = styled(Typography)(() => ({
  fontWeight: 500,
  minWidth: 64,
  textTransform: 'capitalize',
}));

const CoveredStatus = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}));

const MutedText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[50] ?? '#9ca3af',
}));

const WarningText = styled(Typography)(() => ({
  color: '#b91c1c',
}));

const PurchaseOrderList = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
}));

const PurchaseOrderCard = styled('div')(() => ({
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '10px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  cursor: 'pointer',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    borderColor: '#93c5fd',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
  },
}));

const PurchaseOrderHeader = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 8,
}));

const AmountText = styled(Typography)(() => ({
  fontWeight: 700,
}));

const PeriodText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#6b7280',
}));

const TagRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 2,
}));

const Footer = styled('div')(() => ({
  marginTop: 'auto',
  paddingTop: 16,
}));
